import React from "react";
import {
  Modal,
  Button,
  Col,
  Row,
  message,
  notification,
  Progress,
  Upload,
} from "antd";
import axios from "axios";
import styled from "styled-components";
import { compose } from "recompose";
import { withApollo } from "react-apollo";
import { withFirebase } from "../../../../firebase";
import { constants } from "../../../../config";
import { bulkCreateUsersByAgency } from "../../../../graphQl/mutations/affiliate";
import { captureException } from "../../../../utils/sentry";
import { AGENCY_PORTAL_CLASS } from "./style";

const { Dragger } = Upload;

const csvIcon =
  "https://firebasestorage.googleapis.com/v0/b/chazki-link.appspot.com/o/nintendo%2FVector.png?alt=media&token=f0fd9cc9-b8a1-4e68-9bb2-44e2b41fffac";

const successIcon =
  "https://firebasestorage.googleapis.com/v0/b/chazki-link.appspot.com/o/nintendo%2FCheck.png?alt=media&token=4d2d9116-a2b8-4e9a-b562-73619fd8c9a3";

const ContainerDragger = styled(Dragger)`
  background: #ffffff !important;
`;

const styles = {
  button: {
    width: "100%",
    fontFamily: "Gilroy, sans-serif",
    fontWeight: 600,
    fontSize: "14px",
    height: "40px",
  },
  title: {
    fontFamily: "Gilroy, sans-serif",
    fontWeight: 700,
    fontSize: "24px",
    lineHeight: "32px",
    textAlign: "center",
    color: "#595959",
    marginBottom: 10,
  },
};

const getAuthorizationHeader = () => {
  const data = JSON.parse(
    sessionStorage.getItem(
      `oidc.user:${process.env.REACT_APP_AUTH_PROVIDER}:${process.env.REACT_APP_AUTH_CLIENT_ID}`
    )
  );

  if (data && data.access_token && data.token_type) {
    return `${data.token_type} ${data.access_token}`;
  }

  return "";
};

class ModalUploadAffiliatesCsv extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 0,
      file: null,
      response: null,
      successMessage: null,
      loading: false,
      disabled: true,
      uploaded: false,
      validFormats: [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ],
    };
  }

  resetState = () => {
    this.setState({
      step: 0,
      file: null,
      response: null,
      successMessage: null,
      loading: false,
      disabled: true,
      uploaded: false,
    });
  };

  onCancel = () => {
    this.resetState();
    this.props.onClose();
  };

  fileNameStamp = name => {
    const getDate = new Date();
    getDate.setHours(getDate.getHours() - 5);
    let timestamp = getDate.toISOString();
    timestamp = timestamp.replace(/:/g, "-");
    timestamp = timestamp.replace(/\./g, "-");
    return timestamp + name.replace(/ /g, "_");
  };

  beforeUpload = file => {
    const { validFormats } = this.state;
    if (validFormats.indexOf(file.type) >= 0) {
      return true;
    }
    message.error("El archivo no es válido. Solo se permiten archivos CSV.");
    return false;
  };

  customRequest = ({ file, onError, onProgress, onSuccess }) => {
    const { firebase } = this.props;
    const fileStamp = this.fileNameStamp(file.name);
    const key = `csv/${fileStamp}`;
    const task = firebase.refStorage.child(key).put(file);

    task.on(
      "state_changed",
      snapshot => {
        const percentage =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress({ percent: percentage });
      },
      err => {
        message.error("Error al subir archivo");
        captureException(err);
        return onError(err);
      },
      () => {
        task.snapshot.ref.getDownloadURL().then(downloadURL => {
          const { name, uid, url = downloadURL, status = "done" } = file;
          onSuccess({ name, uid, url, status, file: downloadURL });
          this.setState({
            file: {
              url,
              fileStamp,
              filename: file.name,
            },
            disabled: false,
            uploaded: true,
          });
        });
      }
    );
  };

  parseCsvFile = async file => {
    const authorization = getAuthorizationHeader();
    if (!authorization) {
      throw new Error("No se encontró sesión activa. Vuelva a iniciar sesión.");
    }

    if (!constants.cfunctionDomain) {
      throw new Error(
        "El servicio de procesamiento CSV no está configurado (REACT_APP_CFUNCTION_DOMAIN)."
      );
    }

    const body = {
      filename: `csv/${file.fileStamp}`,
      filenameUrl: file.url,
    };

    const { data } = await axios.post(
      `${constants.cfunctionDomain}/uploadAffiliatesByAgencyCsv`,
      body,
      {
        headers: {
          Authorization: authorization,
          "Content-Type": "application/json",
        },
      }
    );

    return data;
  };

  createAffiliates = async users => {
    const { agencyID } = this.props;
    const {
      data: { bulkCreateUsersByAgency: result },
    } = await this.props.client.mutate({
      mutation: bulkCreateUsersByAgency,
      variables: { agencyID: parseInt(agencyID, 10), users },
    });

    return result;
  };

  onContinue = async () => {
    const { step, file } = this.state;

    if (step !== 0) {
      this.onCancel();
      if (step === 1) {
        this.props.onSuccess();
      }
      return;
    }

    if (!file) {
      message.error("Debe seleccionar un archivo CSV.");
      return;
    }

    this.setState({ loading: true });

    try {
      const parseResult = await this.parseCsvFile(file);

      if (parseResult.response === "validation failed") {
        this.setState({
          response: {
            errors: parseResult.errors || [],
            nFailed: parseResult.withErrors || 0,
            nSuccess: parseResult.withoutErrors || 0,
            nTotal:
              (parseResult.withErrors || 0) + (parseResult.withoutErrors || 0),
          },
          loading: false,
          disabled: false,
          step: 2,
        });
        return;
      }

      const users = parseResult.data || [];

      if (!users.length) {
        throw new Error("El archivo CSV no contiene registros para procesar.");
      }

      const mutationResult = await this.createAffiliates(users);

      if (!mutationResult || mutationResult.statusCode !== 200) {
        throw new Error(
          (mutationResult && mutationResult.message) ||
            "Error al crear afiliados."
        );
      }

      this.setState({
        response: {
          nTotal: users.length,
        },
        successMessage: mutationResult.message,
        loading: false,
        disabled: false,
        step: 1,
      });
    } catch (error) {
      captureException(error);
      const errorMessage =
        (error.response &&
          error.response.data &&
          error.response.data.errors &&
          error.response.data.errors.message) ||
        error.message ||
        "Error al procesar el CSV, inténtalo nuevamente.";

      notification.error({ message: errorMessage });
      this.setState({ loading: false });
    }
  };

  render() {
    const { visible } = this.props;
    const { loading, response, file, step, disabled, uploaded, successMessage } =
      this.state;

    return (
      <Modal
        title={null}
        visible={visible}
        footer={null}
        onCancel={this.onCancel}
        width="500px"
        bodyStyle={{ maxHeight: "820px", padding: 40 }}
        centered
        wrapClassName={AGENCY_PORTAL_CLASS}
      >
        <div style={{ textAlign: "center" }}>
          {step === 1 && (
            <img
              alt="Éxito"
              src={successIcon}
              width="70px"
              height="70px"
              style={{ marginBottom: 21 }}
            />
          )}
          <p style={styles.title}>
            {step === 0
              ? "Crear afiliados por CSV"
              : step === 1
              ? "Afiliados creados con éxito"
              : "El CSV contiene errores"}
          </p>
        </div>

        <div
          style={{
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "20px",
            marginBottom: "10px",
            marginTop: "10px",
            textAlign: "center",
            color: "#8C8C8C",
          }}
        >
          {step === 0
            ? 'Antes de subir el archivo CSV, verifica que contenga las columnas: "Nombres", "Apellidos", "Email" y "Telefono".'
            : step === 1
            ? successMessage ||
              `Se procesaron ${response ? response.nTotal : 0} registros.`
            : `Detectamos datos faltantes en ${file ? file.filename : ""}.`}
        </div>

        <Row>
          <Col span={24}>
            {step === 0 ? (
              <ContainerDragger
                beforeUpload={this.beforeUpload}
                customRequest={this.customRequest}
                headers={{ "X-Requested-With": null }}
                showUploadList
                multiple={false}
                accept=".csv"
              >
                {uploaded ? (
                  <div style={{ height: 171, padding: 20, paddingTop: 60 }}>
                    <p>
                      Carga CSV al <b>100%</b>
                    </p>
                    <Progress percent={100} />
                  </div>
                ) : (
                  <>
                    <p className="ant-upload-drag-icon">
                      <img alt="CSV" src={csvIcon} width="32px" height="43px" />
                    </p>
                    <p
                      className="ant-upload-text"
                      style={{
                        color: "#8C8C8C",
                        fontSize: "14px",
                        lineHeight: "20px",
                        marginBottom: "8px",
                      }}
                    >
                      Arrastra el archivo aquí
                    </p>
                    <p
                      className="ant-upload-text"
                      style={{
                        color: "#8C8C8C",
                        fontSize: "14px",
                        lineHeight: "20px",
                        marginBottom: "8px",
                      }}
                    >
                      o
                    </p>
                    <p
                      className="ant-upload-text"
                      style={{
                        fontWeight: 600,
                        fontSize: "14px",
                        lineHeight: "20px",
                        textDecorationLine: "underline",
                        color: "#307FE2",
                        cursor: "pointer",
                      }}
                    >
                      Elige un archivo
                    </p>
                  </>
                )}
              </ContainerDragger>
            ) : step === 1 ? (
              ""
            ) : (
              <>
                <div
                  style={{
                    width: "100%",
                    background: "#F5F5F5",
                    borderRadius: "4px",
                    height: "32px",
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "20px",
                    padding: "6px",
                    marginBottom: "16px",
                    textAlign: "center",
                  }}
                >
                  <span style={{ color: "#CF1322", margin: "0 2px" }}>
                    {`ERRORES: ${(response && response.nFailed) || 0} |`}
                  </span>
                  <span style={{ margin: "0 2px" }}>
                    {`CORRECTOS: ${(response && response.nSuccess) || 0} |`}
                  </span>
                  <span style={{ margin: "0 2px" }}>
                    {`TOTAL: ${(response && response.nTotal) || 0}`}
                  </span>
                </div>

                <div
                  style={{
                    maxHeight: "292px",
                    overflowY: "auto",
                    marginBottom: "26px",
                  }}
                >
                  {response &&
                    response.errors &&
                    response.errors.map((errorItem, index) => (
                      <div
                        key={index.toString()}
                        style={{
                          background: "#FFF1F0",
                          border: "1px solid #CF1322",
                          boxSizing: "border-box",
                          borderRadius: "4px",
                          color: "#CF1322",
                          padding: "8px 16px",
                          marginBottom: "4px",
                        }}
                      >
                        <p
                          style={{
                            fontWeight: 600,
                            fontSize: "12px",
                            lineHeight: "16px",
                            marginBottom: "2px",
                          }}
                        >{`Línea ${errorItem.line}`}</p>
                        <p
                          style={{
                            fontSize: "10px",
                            lineHeight: "12px",
                            marginBottom: "0px",
                          }}
                        >{`${errorItem.label}: ${errorItem.messages}`}</p>
                      </div>
                    ))}
                </div>
              </>
            )}
          </Col>
        </Row>

        <Row
          gutter={24}
          type="flex"
          justify="space-around"
          style={{ marginTop: 15 }}
        >
          {step === 0 && (
            <Col xs={{ span: 24 }} sm={{ span: 12 }} xl={{ span: 12 }}>
              <Button
                type="primary"
                ghost
                style={styles.button}
                onClick={this.onCancel}
              >
                Mejor luego
              </Button>
            </Col>
          )}
          <Col xs={{ span: 24 }} sm={{ span: 12 }} xl={{ span: 12 }}>
            <Button
              type="primary"
              style={styles.button}
              loading={loading}
              disabled={step === 0 ? disabled : false}
              onClick={this.onContinue}
            >
              {step === 0 ? "Crear afiliados" : "Continuar"}
            </Button>
          </Col>
        </Row>
      </Modal>
    );
  }
}

export default compose(withApollo, withFirebase)(ModalUploadAffiliatesCsv);
